import { useEffect, useState, useRef } from 'react';
import Tree from 'react-d3-tree';
import { ChevronUp, ChevronDown, Pencil, Plus, Trash2 } from 'lucide-react';
import ConfirmDeleteModal from '@/components/company/table/ConfirmDeleteModal';

import { IOrgChart } from '@/states/organization.state';
import type { RawNodeDatum } from 'react-d3-tree';

interface OrgChartTreeProps {
  isLoading: boolean;
  treeData: RawNodeDatum[];
  onAddChild?: (node: IOrgChart) => void;
  onEdit?: (node: IOrgChart) => void;
  onSelect: (node: IOrgChart) => void;
  onDelete?: (node: IOrgChart) => void;
  viewMode: 'split' | 'diagram' | 'form';
  initialZoom?: number;
}

export default function OrgChartTree({
  isLoading,
  treeData,
  onAddChild,
  onEdit,
  onSelect,
  onDelete,
  viewMode,
  initialZoom,
}: OrgChartTreeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(initialZoom || 1.0);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteNode, setSelectedDeleteNode] = useState<IOrgChart | null>(null);

  // Center tree whenever container dimensions change or viewMode changes
  useEffect(() => {
    if (!containerRef.current) return;
    const { width } = containerRef.current.getBoundingClientRect();

    // Default centering
    let newTranslate = { x: width / 2, y: 50 };
    let newZoom = initialZoom || 1.0;

    if (viewMode === 'diagram') {
      newZoom = initialZoom || 1.3;
      newTranslate = { x: width / 2, y: 100 };
    } else if (viewMode === 'split') {
      newZoom = initialZoom || 1.0;
      newTranslate = { x: width / 2, y: 100 }; // Center in the split view pane
    }

    setZoom(newZoom);
    setTranslate(newTranslate);
  }, [viewMode, initialZoom]);

  // Handle Resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setTranslate(prev => ({ ...prev, x: width / 2 }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomNode = ({ nodeDatum, toggleNode, hierarchyPointNode }: any) => {
    const childCount = nodeDatum.children?.length || 0;
    const isRoot = hierarchyPointNode.depth === 0;

    return (
      <g
        onClick={() => {
          if (onSelect) onSelect(nodeDatum as IOrgChart);
        }}
        className="cursor-pointer"
      >
        <rect width="180" height="45" x="-90" y="-22.5" rx="8" ry="8" fill="#fff" stroke="#9E9E9E" strokeWidth={1} />
        <foreignObject x="-85" y="-20" width="170" height="40">
          <div className="flex items-center justify-between h-full px-2 text-[12px] text-slate-800 font-medium">
            <div className="flex items-center gap-1">
              {childCount > 0 && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleNode();
                  }}
                >
                  {nodeDatum.__rd3t.collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </button>
              )}
              <span className="break-words max-w-[90px] leading-tight">{nodeDatum.name}</span>
              {childCount > 0 && (
                <span className="ml-1 mr-1 px-2 py-1 rounded-md bg-slate-200 text-xs">{childCount}</span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {onEdit && (
                <button
                  className="text-primary hover:text-primary/70"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(nodeDatum as IOrgChart);
                  }}
                >
                  <Pencil size={14} strokeWidth={2.5} />
                </button>
              )}

              {onAddChild && (
                <button
                  className="w-4 h-4 flex items-center justify-center rounded-full border border-green-600 text-green-600 hover:text-green-400 hover:border-green-400 transition"
                  onClick={e => {
                    e.stopPropagation();
                    onAddChild(nodeDatum as IOrgChart);
                  }}
                >
                  <Plus size={10} strokeWidth={2.5} />
                </button>
              )}

              {!isRoot && onDelete && (
                <button
                  className="text-red-600 hover:text-red-400 transition"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedDeleteNode(nodeDatum as IOrgChart);
                    setDeleteModalOpen(true);
                  }}
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  if (isLoading) {
    return (
      <div className="mt-20 flex justify-center">
        <p className="text-gray-400 text-sm mt-5">Loading organization chart...</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center overflow-x-auto transition-all duration-300">
      {!treeData || treeData.length === 0 ? (
        <p className="text-gray-400 text-sm mt-5">No org chart found.</p>
      ) : (
        <div ref={containerRef} style={{ width: '100%', height: '70vh' }}>
          <Tree
            data={treeData}
            orientation="vertical"
            translate={translate}
            pathFunc="step"
            renderCustomNodeElement={renderCustomNode}
            separation={{ siblings: 1.5, nonSiblings: 1.5 }}
            zoom={zoom}
            scaleExtent={{ min: 0.1, max: 2 }}
          />
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedDeleteNode(null);
        }}
        onConfirm={() => {
          if (onDelete && selectedDeleteNode) {
            onDelete(selectedDeleteNode);
          }
          setDeleteModalOpen(false);
          setSelectedDeleteNode(null);
        }}
      />
    </div>
  );
}
